/**
 * YEŞİLKÖŞE KAFE - SUPABASE CLOUD & REALTIME CLIENT
 */
import { createClient as supabaseCreateClient } from '@supabase/supabase-js';

const DEFAULT_SUPABASE_URL = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_URL) || (typeof window !== 'undefined' && window.SUPABASE_URL) || localStorage.getItem('yk_supabase_url') || 'https://aiaskmhghewfmyjuueze.supabase.co';
const DEFAULT_SUPABASE_KEY = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY) || (typeof window !== 'undefined' && window.SUPABASE_KEY) || localStorage.getItem('yk_supabase_key') || 'sb_publishable_jX5yekLtMIok_e0bMhUg3Q_ZjxImh_l';

export let supabase = null;
export let isCloudConnected = false;

function tryInitSupabase(url, key) {
  if (url && key) {
    try {
      supabase = supabaseCreateClient(url, key);
      isCloudConnected = true;
      console.log('⚡ Supabase Cloud Realtime Connected!', url);
      return true;
    } catch (e) {
      console.warn('Supabase initialization failed:', e);
    }
  }
  return false;
}

// Initialize immediately with default credentials
tryInitSupabase(DEFAULT_SUPABASE_URL, DEFAULT_SUPABASE_KEY);

export function initSupabaseConfig(url, key) {
  const success = tryInitSupabase(url, key);
  if (success) {
    localStorage.setItem('yk_supabase_url', url);
    localStorage.setItem('yk_supabase_key', key);
  }
  return success;
}

// --- EVENT REQUESTS CLOUD APIS ---
export async function verifyCloudAdminPin(pin) {
  if (!supabase || !pin) return false;
  try {
    const { data, error } = await supabase.rpc('get_admin_event_requests', { p_pin: String(pin).trim() });
    if (error) {
      return false;
    }
    return Array.isArray(data);
  } catch (e) {
    return false;
  }
}

export async function fetchCloudEventRequests(pin = null) {
  if (!isCloudConnected || !supabase) return null;
  try {
    const adminPin = pin || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('yk_admin_pin') : null) || (typeof localStorage !== 'undefined' ? localStorage.getItem('yk_admin_pin') : null);
    if (!adminPin) return null;

    // 1. Try secure RPC function (protected by PIN)
    try {
      const { data: rpcData, error: rpcErr } = await supabase.rpc('get_admin_event_requests', { p_pin: adminPin });

      if (!rpcErr && Array.isArray(rpcData)) {
        return rpcData.map(r => ({
          id: r.id,
          fullName: r.full_name,
          phone: r.phone,
          eventType: r.event_type,
          eventTypeName: r.event_type_name,
          eventArea: r.event_area || '',
          date: r.event_date,
          guests: r.guests,
          notes: r.notes,
          status: r.status,
          createdAt: new Date(r.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        }));
      }
    } catch (_) {}

    // 2. Fallback to standard SELECT if RPC not configured yet
    const { data, error } = await supabase
      .from('event_requests')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(r => ({
      id: r.id,
      fullName: r.full_name,
      phone: r.phone,
      eventType: r.event_type,
      eventTypeName: r.event_type_name,
      eventArea: r.event_area || '',
      date: r.event_date,
      guests: r.guests,
      notes: r.notes,
      status: r.status,
      createdAt: new Date(r.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }));
  } catch (e) {
    console.warn('Cloud fetch event_requests failed:', e);
    return null;
  }
}

export async function saveCloudEventRequest(req) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const { error } = await supabase.from('event_requests').insert([{
      id: req.id,
      full_name: req.fullName,
      phone: req.phone,
      event_type: req.eventType,
      event_type_name: req.eventTypeName,
      event_area: req.eventArea || '',
      event_date: req.date,
      guests: req.guests,
      notes: req.notes,
      status: req.status || 'new'
    }]);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud save event_requests failed:', e);
    return false;
  }
}

export async function updateCloudEventRequestStatus(id, newStatus, pin = null) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const adminPin = pin || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('yk_admin_pin') : null) || (typeof localStorage !== 'undefined' ? localStorage.getItem('yk_admin_pin') : null);
    // 1. Try secure RPC
    try {
      if (adminPin) {
        const { data, error: rpcErr } = await supabase.rpc('update_admin_event_request_status', { p_pin: adminPin, p_id: id, p_status: newStatus });
        if (!rpcErr && data === true) return true;
      }
    } catch (_) {}

    // 2. Fallback to direct update
    const { error } = await supabase
      .from('event_requests')
      .update({ status: newStatus })
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud update status failed:', e);
    return false;
  }
}

export async function deleteCloudEventRequest(id, pin = null) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const adminPin = pin || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('yk_admin_pin') : null) || (typeof localStorage !== 'undefined' ? localStorage.getItem('yk_admin_pin') : null);
    // 1. Try secure RPC
    try {
      if (adminPin) {
        const { data, error: rpcErr } = await supabase.rpc('delete_admin_event_request', { p_pin: adminPin, p_id: id });
        if (!rpcErr && data === true) return true;
      }
    } catch (_) {}

    // 2. Fallback to direct delete
    const { error } = await supabase
      .from('event_requests')

      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud delete event_requests failed:', e);
    return false;
  }
}

// --- TELEGRAM NOTIFICATIONS ---
export async function sendTelegramNotification(req) {
  const token = localStorage.getItem('yk_telegram_bot_token');
  const chatId = localStorage.getItem('yk_telegram_chat_id');
  if (!token || !chatId) return false;

  const text = `🔔 *YENİ ORGANİZASYON TEKLİF TALEBİ!*
━━━━━━━━━━━━━━━━━━━
👤 *Müşteri:* ${req.fullName}
📞 *Telefon:* ${req.phone}
🎉 *Etkinlik:* ${req.eventTypeName || req.eventType}
📍 *Seçilen Alan:* ${req.eventArea || 'Belirtilmedi'}
📅 *Tarih:* ${req.date}
👥 *Kişi Sayısı:* ${req.guests} Kişi
${req.notes ? `📝 *Not:* ${req.notes}\n` : ''}━━━━━━━━━━━━━━━━━━━
⏰ _Kayıt: ${req.createdAt || new Date().toLocaleTimeString('tr-TR')}_
🏢 _Yeşilköşe Kafe Web Sistemi_`;

  try {
    const res = await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        text: text,
        parse_mode: 'Markdown'
      })
    });
    return res.ok;
  } catch (err) {
    console.warn('Telegram send error:', err);
    return false;
  }
}

export async function testTelegramNotification(token, chatId) {
  const text = `🟢 *Yeşilköşe Kafe - Telegram Bildirim Testi*
━━━━━━━━━━━━━━━━━━━
Tebrikler! Telegram bot bağlantınız başarıyla kuruldu.
Artık web sitenizden gelen tüm organizasyon teklifleri anında buraya bildirim olarak düşecektir.
⏰ _Test Zamanı: ${new Date().toLocaleTimeString('tr-TR')}_`;

  try {
    const res = await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        text: text,
        parse_mode: 'Markdown'
      })
    });
    const data = await res.json();
    return { ok: res.ok, data };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

// --- TABLE CALLS CLOUD APIS ---
export async function fetchCloudTableCalls() {
  if (!isCloudConnected || !supabase) return null;
  try {
    const { data, error } = await supabase
      .from('table_calls')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data.map(c => ({
      id: c.id,
      tableId: c.table_id,
      tableName: c.table_name,
      type: c.type,
      deviceId: c.device_id,
      timestamp: c.timestamp
    }));
  } catch (e) {
    console.warn('Cloud fetch table_calls failed:', e);
    return null;
  }
}

export async function saveCloudTableCall(call) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const { error } = await supabase.from('table_calls').insert([{
      id: call.id,
      table_id: call.tableId,
      table_name: call.tableName,
      type: call.type,
      device_id: call.deviceId,
      timestamp: call.timestamp
    }]);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud save table_call failed:', e);
    return false;
  }
}

export async function deleteCloudTableCall(id) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const { error } = await supabase
      .from('table_calls')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud delete table_call failed:', e);
    return false;
  }
}

// --- TABLE ADISYONS CLOUD APIS ---
export async function fetchCloudAdisyons() {
  if (!isCloudConnected || !supabase) return null;
  try {
    const { data, error } = await supabase
      .from('table_adisyons')
      .select('*');

    if (error) throw error;
    const adisyonsMap = {};
    data.forEach(row => {
      adisyonsMap[row.table_id] = {
        items: row.items || [],
        total: Number(row.total) || 0
      };
    });
    return adisyonsMap;
  } catch (e) {
    console.warn('Cloud fetch table_adisyons failed:', e);
    return null;
  }
}

export async function saveCloudAdisyon(tableId, items, total) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const { error } = await supabase.from('table_adisyons').upsert([{
      table_id: tableId,
      items: items,
      total: total,
      updated_at: new Date().toISOString()
    }]);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud save table_adisyon failed:', e);
    return false;
  }
}

export async function deleteCloudAdisyon(tableId) {
  if (!isCloudConnected || !supabase) return false;
  try {
    const { error } = await supabase
      .from('table_adisyons')
      .delete()
      .eq('table_id', tableId);

    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud delete table_adisyon failed:', e);
    return false;
  }
}

// --- PRODUCTS CLOUD APIS ---
export async function fetchCloudProducts() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('display_order', { ascending: true });
    if (error) throw error;
    return data.map(p => ({
      id: p.id,
      name: p.name,
      category: p.category,
      price: Number(p.price) || 0,
      desc: p.description || '',
      imageUrl: p.image_url || '',
      image: p.image_url || '',
      isOutOfStock: p.is_out_of_stock || false,
      tags: p.tags || [],
      options: p.options || null,
      displayOrder: p.display_order || 0
    }));
  } catch (e) {
    console.warn('Cloud fetch products failed:', e);
    return null;
  }
}

export async function upsertCloudProduct(product) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('products').upsert([{
      id: product.id,
      name: product.name,
      category: product.category,
      price: product.price,
      description: product.desc || '',
      image_url: product.imageUrl || product.image || '',
      is_out_of_stock: product.isOutOfStock || false,
      tags: product.tags || [],
      options: product.options || null,
      display_order: product.displayOrder || 0
    }], { onConflict: 'id' });
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud upsert product failed:', e);
    return false;
  }
}

export async function deleteCloudProduct(productId) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('products').delete().eq('id', productId);
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud delete product failed:', e);
    return false;
  }
}

// --- CATEGORIES CLOUD APIS ---
export async function fetchCloudCategories() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('display_order', { ascending: true });
    if (error) throw error;
    return data.map(c => ({
      id: c.id,
      name: c.name,
      emoji: c.emoji || '🍽️',
      displayOrder: c.display_order || 0
    }));
  } catch (e) {
    console.warn('Cloud fetch categories failed:', e);
    return null;
  }
}

export async function upsertCloudCategory(category) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('categories').upsert([{
      id: category.id,
      name: category.name,
      emoji: category.emoji || '🍽️',
      display_order: category.displayOrder || 0
    }], { onConflict: 'id' });
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud upsert category failed:', e);
    return false;
  }
}

export async function deleteCloudCategory(categoryId) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('categories').delete().eq('id', categoryId);
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud delete category failed:', e);
    return false;
  }
}

// --- TABLES & AREAS CLOUD APIS ---
export async function fetchCloudTables() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .order('display_order', { ascending: true });
    if (error) throw error;
    return data.map(t => ({
      id: t.id,
      name: t.name,
      areaId: t.area_id,
      status: t.status || 'empty',
      token: t.token,
      displayOrder: t.display_order || 0
    }));
  } catch (e) {
    console.warn('Cloud fetch tables failed:', e);
    return null;
  }
}

export async function upsertCloudTable(table) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('tables').upsert([{
      id: table.id,
      name: table.name,
      area_id: table.areaId,
      status: table.status || 'empty',
      token: table.token,
      display_order: table.displayOrder || 0
    }], { onConflict: 'id' });
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud upsert table failed:', e);
    return false;
  }
}

export async function deleteCloudTable(tableId) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('tables').delete().eq('id', tableId);
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud delete table failed:', e);
    return false;
  }
}

export async function fetchCloudAreas() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from('areas')
      .select('*')
      .order('display_order', { ascending: true });
    if (error) throw error;
    return data.map(a => ({
      id: a.id,
      name: a.name,
      icon: a.icon || 'ri-map-pin-line',
      displayOrder: a.display_order || 0
    }));
  } catch (e) {
    console.warn('Cloud fetch areas failed:', e);
    return null;
  }
}

export async function upsertCloudArea(area) {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('areas').upsert([{
      id: area.id,
      name: area.name,
      icon: area.icon || 'ri-map-pin-line',
      display_order: area.displayOrder || 0
    }], { onConflict: 'id' });
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('Cloud upsert area failed:', e);
    return false;
  }
}

// --- REALTIME SUBSCRIPTIONS ---
let _realtimeChannel = null;
let _realtimeCallbacks = { onNewCall: null, onNewEventRequest: null, onAdisyonChange: null };

export function subscribeRealtimeEvents(onNewCall, onNewEventRequest, onAdisyonChange) {
  if (!supabase) {
    console.warn('🔴 Supabase not initialized, cannot subscribe to realtime');
    return null;
  }

  // Store callbacks for potential retry
  _realtimeCallbacks = { onNewCall, onNewEventRequest, onAdisyonChange };

  // Remove any existing channel before creating new one
  if (_realtimeChannel) {
    try { supabase.removeChannel(_realtimeChannel); } catch (_) {}
    _realtimeChannel = null;
  }

  _realtimeChannel = supabase
    .channel('yesilkose_rt_' + Date.now())
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'table_calls' }, (payload) => {
      console.log('📞 Realtime table_calls INSERT:', payload.new);
      if (onNewCall) onNewCall(payload.new);
    })
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'event_requests' }, (payload) => {
      console.log('🎉 Realtime event_requests INSERT:', payload.new);
      if (onNewEventRequest) onNewEventRequest(payload.new);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'table_adisyons' }, (payload) => {
      if (onAdisyonChange) onAdisyonChange(payload);
    })
    .subscribe((status, err) => {
      console.log('🔌 Supabase Realtime status:', status, err || '');
      if (status === 'SUBSCRIBED') {
        console.log('✅ Realtime channel SUBSCRIBED — dinleniyor!');
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('⚠️ Realtime channel error, 5 sn sonra yeniden deneniyor...', err);
        setTimeout(() => {
          subscribeRealtimeEvents(
            _realtimeCallbacks.onNewCall,
            _realtimeCallbacks.onNewEventRequest,
            _realtimeCallbacks.onAdisyonChange
          );
        }, 5000);
      } else if (status === 'CLOSED') {
        console.log('🔴 Realtime channel CLOSED');
      }
    });

  return _realtimeChannel;
}
